import { Injectable } from '@angular/core';

export type ToastType = 'error' | 'success' | 'warning' | 'info';

export interface ToastOptions {
  title?: string;
  duration?: number;
}

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private containerId = 'gymo-toast-container';

  /**
   * Méthode standard GYMO pour compatibilité totale avec les composants existants
   */
  showToast(message: any, isError: boolean = false, duration?: number, title?: string): void {
    const type: ToastType = isError ? 'error' : 'success';
    this.displayToast(message, type, duration, title);
  }

  /**
   * Affichage d'un toast d'erreur professionnel
   */
  error(message: any, options?: ToastOptions): void {
    this.displayToast(message, 'error', options?.duration, options?.title);
  }

  /**
   * Affichage d'un toast de succès
   */
  success(message: any, options?: ToastOptions): void {
    this.displayToast(message, 'success', options?.duration, options?.title);
  }

  /**
   * Affichage d'un toast d'avertissement
   */
  warning(message: any, options?: ToastOptions): void {
    this.displayToast(message, 'warning', options?.duration, options?.title);
  }

  /**
   * Affichage d'un toast d'information
   */
  info(message: any, options?: ToastOptions): void {
    this.displayToast(message, 'info', options?.duration, options?.title);
  }

  /**
   * Fermer un toast avec une animation fluide
   */
  closeToast(toast: HTMLElement): void {
    if (!toast || toast.dataset['closing'] === 'true') return;
    toast.dataset['closing'] = 'true';

    toast.classList.remove('opacity-100', 'translate-y-0', 'scale-100');
    toast.classList.add('opacity-0', '-translate-y-2', 'scale-95');

    setTimeout(() => {
      toast.remove();
      const container = document.getElementById(this.containerId);
      if (container && container.children.length === 0) {
        container.remove();
      }
    }, 220);
  }

  /**
   * Fermer tous les toasts actifs
   */
  clearAll(): void {
    const container = document.getElementById(this.containerId);
    if (!container) return;
    const toasts = Array.from(container.children) as HTMLElement[];
    toasts.forEach((t) => this.closeToast(t));
  }

  private displayToast(
    rawMessage: any,
    type: ToastType,
    customDuration?: number,
    customTitle?: string
  ): void {
    const container = this.getOrCreateContainer();

    // Limiter à 4 toasts simultanés pour ne pas encombrer l'écran
    if (container.children.length >= 4) {
      const oldest = container.firstElementChild as HTMLElement;
      if (oldest) {
        this.closeToast(oldest);
      }
    }

    const { title: parsedTitle, message } = this.parseContent(rawMessage);
    const resolvedTitle = customTitle || parsedTitle || this.getDefaultTitle(type, message);

    // Durée par défaut confortable (5s pour erreur, 3.8s pour succès)
    let duration = customDuration;
    if (!duration || duration <= 1000) {
      duration = type === 'error' ? 5000 : type === 'warning' ? 4500 : 3800;
    }

    const toast = document.createElement('div');
    toast.setAttribute('role', type === 'error' ? 'alert' : 'status');
    toast.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite');

    const config = this.getTypeConfig(type);

    toast.className = [
      'pointer-events-auto',
      'relative',
      'w-full',
      'rounded-2xl',
      'bg-white/95',
      'dark:bg-slate-900/95',
      'backdrop-blur-xl',
      'border',
      'border-slate-200/90',
      'dark:border-slate-800/90',
      'shadow-[0_14px_36px_-6px_rgba(0,0,0,0.12),0_4px_12px_rgba(0,0,0,0.06)]',
      'dark:shadow-[0_18px_42px_-6px_rgba(0,0,0,0.55)]',
      'p-3.5',
      'sm:p-4',
      'overflow-hidden',
      'opacity-0',
      '-translate-y-3',
      'scale-95',
      'transition-all',
      'duration-250',
      'ease-out',
      'flex',
      'flex-col',
      'gap-2',
      config.borderAccent,
    ].join(' ');

    const safeTitle = this.escapeHtml(resolvedTitle);
    const safeMessage = this.escapeHtml(message);

    toast.innerHTML = `
      <div class="flex items-start gap-3 w-full">
        <!-- Badge Icône Stylisé -->
        <div class="flex-shrink-0 w-8.5 h-8.5 rounded-xl ${config.iconBg} ${config.iconBorder} ${config.iconColor} border flex items-center justify-center mt-0.5 shadow-xs">
          ${config.iconSvg}
        </div>

        <!-- Corps du Message -->
        <div class="flex-1 min-w-0 pr-1">
          <div class="flex items-center gap-2">
            <h4 class="font-semibold text-[13.5px] sm:text-sm tracking-tight text-slate-900 dark:text-slate-100 truncate">
              ${safeTitle}
            </h4>
          </div>
          <div class="text-[13px] leading-relaxed text-slate-600 dark:text-slate-300 font-normal mt-0.5 break-words max-h-36 overflow-y-auto pr-1 select-text">
            ${safeMessage}
          </div>
        </div>

        <!-- Bouton Fermer Accessible -->
        <button 
          type="button" 
          class="flex-shrink-0 -mr-1 -mt-0.5 p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-colors cursor-pointer focus:outline-hidden focus:ring-2 focus:ring-slate-300 dark:focus:ring-slate-700" 
          aria-label="Fermer la notification"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <!-- Barre de progression de lecture -->
      <div class="absolute bottom-0 left-0 right-0 h-[2.5px] bg-slate-100 dark:bg-slate-800/80 overflow-hidden">
        <div class="toast-progress-bar h-full w-full origin-left ${config.progressBg} transition-transform linear"></div>
      </div>
    `;

    container.appendChild(toast);

    const closeBtn = toast.querySelector('button');
    closeBtn?.addEventListener('click', () => this.closeToast(toast));

    const progressBar = toast.querySelector('.toast-progress-bar') as HTMLElement;

    // Déclenchement de l'animation d'entrée
    requestAnimationFrame(() => {
      toast.classList.remove('opacity-0', '-translate-y-3', 'scale-95');
      toast.classList.add('opacity-100', 'translate-y-0', 'scale-100');

      if (progressBar) {
        progressBar.style.transition = `transform ${duration}ms linear`;
        progressBar.style.transform = 'scaleX(0)';
      }
    });

    // Gestion du timer avec mise en pause au survol
    let remaining = duration;
    let startTime = Date.now();
    let timeoutId: any;

    const startTimer = () => {
      startTime = Date.now();
      timeoutId = setTimeout(() => {
        this.closeToast(toast);
      }, remaining);
    };

    toast.addEventListener('mouseenter', () => {
      clearTimeout(timeoutId);
      const elapsed = Date.now() - startTime;
      remaining = Math.max(0, remaining - elapsed);

      if (progressBar) {
        const currentScale = duration > 0 ? remaining / duration : 0;
        progressBar.style.transition = 'none';
        progressBar.style.transform = `scaleX(${currentScale})`;
      }
    });

    toast.addEventListener('mouseleave', () => {
      if (remaining > 50) {
        if (progressBar) {
          progressBar.style.transition = `transform ${remaining}ms linear`;
          progressBar.style.transform = 'scaleX(0)';
        }
        startTimer();
      } else {
        this.closeToast(toast);
      }
    });

    startTimer();
  }

  private getOrCreateContainer(): HTMLElement {
    let container = document.getElementById(this.containerId);
    if (!container) {
      container = document.createElement('div');
      container.id = this.containerId;
      container.className =
        'fixed top-4 left-4 right-4 sm:left-auto sm:right-6 sm:top-6 z-[99999] flex flex-col gap-2.5 max-w-[420px] sm:w-[420px] pointer-events-none transition-all duration-300';
      document.body.appendChild(container);
    }
    return container;
  }

  private parseContent(raw: any): { title?: string; message: string } {
    if (raw === null || raw === undefined) {
      return { message: 'Une erreur inattendue est survenue' };
    }

    // Chaîne de caractères
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      // Format "Titre : Message"
      const colonIndex = trimmed.indexOf(' : ');
      if (colonIndex > 2 && colonIndex < 35 && !trimmed.includes('\n')) {
        return {
          title: trimmed.substring(0, colonIndex).trim(),
          message: trimmed.substring(colonIndex + 3).trim(),
        };
      }
      return { message: trimmed };
    }

    // Tableau de messages d'erreur (ex: validation API)
    if (Array.isArray(raw)) {
      const messages = raw
        .map((item) => (typeof item === 'string' ? item : this.parseContent(item).message))
        .filter(Boolean);
      return { message: messages.join('\n') || 'Une erreur est survenue' };
    }

    // Objet d'erreur API ou standard
    if (typeof raw === 'object') {
      if (typeof raw.message === 'string' && raw.message.trim()) {
        return { message: raw.message.trim() };
      }
      if (typeof raw.error === 'string' && raw.error.trim()) {
        return { message: raw.error.trim() };
      }
      if (raw.error && typeof raw.error.message === 'string') {
        return { message: raw.error.message.trim() };
      }
      if (typeof raw.detail === 'string' && raw.detail.trim()) {
        return { message: raw.detail.trim() };
      }
      if (raw.errors && typeof raw.errors === 'object') {
        const errorList = Object.values(raw.errors).flat().join(', ');
        if (errorList) return { message: errorList };
      }
      try {
        return { message: JSON.stringify(raw) };
      } catch {
        return { message: 'Erreur inattendue' };
      }
    }

    return { message: String(raw) };
  }

  private getDefaultTitle(type: ToastType, message: string): string {
    const lower = (message || '').toLowerCase();

    if (type === 'error') {
      if (lower.includes('session') || lower.includes('expiré') || lower.includes('non autorisé')) {
        return 'Session expirée';
      }
      if (lower.includes('identifiant') || lower.includes('mot de passe') || lower.includes('connexion')) {
        return 'Échec de connexion';
      }
      if (lower.includes('remplir') || lower.includes('requis') || lower.includes('champ')) {
        return 'Champs requis';
      }
      if (lower.includes('introuvable') || lower.includes('non trouvé') || lower.includes('404')) {
        return 'Élément introuvable';
      }
      if (lower.includes('réseau') || lower.includes('connexion internet') || lower.includes('serveur')) {
        return 'Erreur de communication';
      }
      return 'Une erreur est survenue';
    }

    if (type === 'success') {
      if (lower.includes('créé') || lower.includes('création')) return 'Création réussie';
      if (lower.includes('modifié') || lower.includes('mise à jour') || lower.includes('enregistr')) return 'Enregistré avec succès';
      if (lower.includes('supprimé')) return 'Suppression confirmée';
      return 'Opération réussie';
    }

    if (type === 'warning') return 'Attention';
    return 'Information';
  }

  private getTypeConfig(type: ToastType) {
    switch (type) {
      case 'error':
        return {
          borderAccent: 'border-l-4 border-l-rose-500',
          iconBg: 'bg-rose-50 dark:bg-rose-500/15',
          iconBorder: 'border-rose-200/80 dark:border-rose-500/25',
          iconColor: 'text-rose-600 dark:text-rose-400',
          progressBg: 'bg-rose-500',
          iconSvg: `
            <svg class="w-4.5 h-4.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          `,
        };
      case 'success':
        return {
          borderAccent: 'border-l-4 border-l-emerald-500',
          iconBg: 'bg-emerald-50 dark:bg-emerald-500/15',
          iconBorder: 'border-emerald-200/80 dark:border-emerald-500/25',
          iconColor: 'text-emerald-600 dark:text-emerald-400',
          progressBg: 'bg-emerald-500',
          iconSvg: `
            <svg class="w-4.5 h-4.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          `,
        };
      case 'warning':
        return {
          borderAccent: 'border-l-4 border-l-amber-500',
          iconBg: 'bg-amber-50 dark:bg-amber-500/15',
          iconBorder: 'border-amber-200/80 dark:border-amber-500/25',
          iconColor: 'text-amber-600 dark:text-amber-400',
          progressBg: 'bg-amber-500',
          iconSvg: `
            <svg class="w-4.5 h-4.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
          `,
        };
      case 'info':
      default:
        return {
          borderAccent: 'border-l-4 border-l-brand-500',
          iconBg: 'bg-orange-50 dark:bg-orange-500/15',
          iconBorder: 'border-orange-200/80 dark:border-orange-500/25',
          iconColor: 'text-brand-500 dark:text-brand-400',
          progressBg: 'bg-brand-500',
          iconSvg: `
            <svg class="w-4.5 h-4.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
          `,
        };
    }
  }

  private escapeHtml(str: string): string {
    if (!str) return '';
    const p = document.createElement('p');
    p.textContent = str;
    return p.innerHTML.replace(/\n/g, '<br>');
  }
}